import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetchMtrSpecList } from '#/api/imsMgmt/settings/mtrSpec/fetchList';
import { DEFAULT_LIST_PAGE_SIZE } from '#/utils/listUrl/listPaginationDefaults';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import { mtrSpecListQueryKey } from '#/utils/queryKeys';

const EMPTY_PAGE = {
  current_page: 1,
  page_size: DEFAULT_LIST_PAGE_SIZE,
  total_count: 0,
  total_page: 1,
};

const DEFAULT_LIST_QUERY = {
  page: 1,
  pageSize: DEFAULT_LIST_PAGE_SIZE,
  keyword: '',
  sort: [],
  filters: {},
};

function useMtrSpecList(specType) {
  const [listQueryByType, setListQueryByType] = useState({
    specType: null,
    query: DEFAULT_LIST_QUERY,
  });

  const listQuery = listQueryByType.specType === specType
    ? listQueryByType.query
    : DEFAULT_LIST_QUERY;

  const updateListQuery = useCallback((updater) => {
    setListQueryByType((prev) => {
      const current = prev.specType === specType ? prev.query : DEFAULT_LIST_QUERY;
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { specType, query: next };
    });
  }, [specType]);

  const apiListQuery = useMemo(() => ({
    ...listQuery,
    filters: {
      ...listQuery.filters,
      specType,
    },
  }), [listQuery, specType]);

  const query = useQuery({
    queryKey: mtrSpecListQueryKey(apiListQuery),
    queryFn: () => fetchMtrSpecList(apiListQuery),
    placeholderData: keepPreviousData,
  });

  const setPage = useCallback((page) => {
    updateListQuery((prev) => ({ ...prev, page }));
  }, [updateListQuery]);

  const setPageSize = useCallback((pageSize) => {
    updateListQuery((prev) => ({ ...prev, pageSize, page: 1 }));
  }, [updateListQuery]);

  const setKeyword = useCallback((keyword) => {
    updateListQuery((prev) => ({ ...prev, keyword, page: 1 }));
  }, [updateListQuery]);

  const setFilter = useCallback((key, value) => {
    updateListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: mergeFilterUpdate(prev.filters, key, value),
    }));
  }, [updateListQuery]);

  const removeSortAt = useCallback((index) => {
    updateListQuery((prev) => ({
      ...prev,
      page: 1,
      sort: (prev.sort ?? []).filter((_, sortIndex) => sortIndex !== index),
    }));
  }, [updateListQuery]);

  const resetListQuery = useCallback(() => {
    updateListQuery((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      keyword: '',
      sort: [],
      filters: {},
    }));
  }, [updateListQuery]);

  return {
    rows: query.data?.data ?? [],
    pagination: query.data?.page ?? EMPTY_PAGE,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
    refetch: query.refetch,
  };
}

export default useMtrSpecList;
